import { parse } from 'date-fns';

export interface CalendarEvent {
    date: string;
    startTime: string;
    endTime: string;
    title: string;
    description: string;
    location: string;
}

// Helper function to format date for ICS file
const formatICSDate = (date: string, time: string): string => {
    const dateObj = parse(date, 'yyyy-MM-dd', new Date());
    const timeObj = parse(time, 'HH:mm:ss', new Date());

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(timeObj.getHours()).padStart(2, '0');
    const minutes = String(timeObj.getMinutes()).padStart(2, '0');

    return `${year}${month}${day}T${hours}${minutes}00`;
};

// Generate ICS file content
const generateICS = (event: CalendarEvent): string => {
    const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Beauty Salon//Appointment//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `UID:${Date.now()}@beautysalon.com`,
        `DTSTAMP:${formatICSDate(event.date, event.startTime)}`,
        `DTSTART:${formatICSDate(event.date, event.startTime)}`,
        `DTEND:${formatICSDate(event.date, event.endTime)}`,
        `SUMMARY:${event.title}`,
        `DESCRIPTION:${event.description}`,
        `LOCATION:${event.location}`,
        'BEGIN:VALARM',
        'TRIGGER:-PT60M',
        'ACTION:DISPLAY',
        'DESCRIPTION:Reminder',
        'END:VALARM',
        'STATUS:CONFIRMED',
        'SEQUENCE:0',
        'END:VEVENT',
        'END:VCALENDAR'
    ].join('\r\n');

    return icsContent;
};

// Download ICS file
export const downloadICS = (event: CalendarEvent): void => {
    const icsContent = generateICS(event);
    
    // Detect iOS devices
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
        // For iOS: Use data URI to trigger Calendar app
        const dataUri = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
        window.location.href = dataUri;
    } else {
        // For other devices: Download as file
        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = 'appointment.ics';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    }
};

// Generate Google Calendar URL
export const getGoogleCalendarUrl = (event: CalendarEvent): string => {
    const startDateTime = `${event.date.replace(/-/g, '')}T${event.startTime.replace(/:/g, '')}`;
    const endDateTime = `${event.date.replace(/-/g, '')}T${event.endTime.replace(/:/g, '')}`;

    const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: event.title,
        details: event.description,
        location: event.location,
        dates: `${startDateTime}/${endDateTime}`
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Generate Outlook.com URL
export const getOutlookUrl = (event: CalendarEvent): string => {
    const startDateTime = `${event.date}T${event.startTime}`;
    const endDateTime = `${event.date}T${event.endTime}`;

    const params = new URLSearchParams({
        path: '/calendar/action/compose',
        rru: 'addevent',
        subject: event.title,
        body: event.description,
        location: event.location,
        startdt: startDateTime,
        enddt: endDateTime
    });

    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

export const addToGoogleCalendar = (event: CalendarEvent): void => {
    window.open(getGoogleCalendarUrl(event), '_blank');
};

export const addToOutlookCalendar = (event: CalendarEvent): void => {
    window.open(getOutlookUrl(event), '_blank');
};

export const addToAppleCalendar = (event: CalendarEvent): void => {
    downloadICS(event);
};